/**
 * Maps API, network, and Supabase/PostgREST errors to safe user-visible strings.
 * Use for Redux rejectWithValue, toasts, and inline error state — never surface raw 503/PGRST/Postgres text.
 */
import { ERROR_MESSAGES } from '../constants/errorMessages';
const DEFAULT_MAX_LEN = 500;
/** Heuristic: API validation messages are safe; Postgres/PostgREST/internal fragments are not */
export function isLikelySafeApiMessage(msg) {
    if (!msg || typeof msg !== 'string')
        return false;
    const t = msg.trim();
    if (t.length === 0 || t.length > 2000)
        return false;
    if (/PGRST\d+|42P\d+|SQLSTATE|postgresql|syntax error at|ECONNREFUSED|ECONNRESET|does not exist/i.test(t)) {
        return false;
    }
    if (/^\s*\d{3}\s+(?:Error|error)/.test(t))
        return false;
    return true;
}
function truncateMessage(msg, maxLen) {
    if (msg.length <= maxLen)
        return msg;
    return `${msg.slice(0, maxLen - 1)}…`;
}
function extractHttpMessage(data) {
    if (!data || typeof data !== 'object')
        return undefined;
    const d = data;
    if (typeof d.message === 'string' && d.message.trim())
        return d.message;
    if (typeof d.error === 'string' && d.error.trim())
        return d.error;
    return undefined;
}
function isNetworkLikeError(error) {
    if (!error || typeof error !== 'object')
        return false;
    const e = error;
    const code = String(e.code ?? '');
    const name = String(e.name ?? '');
    const msg = String(e.message ?? '');
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT')
        return true;
    if (name === 'NetworkError')
        return true;
    if (msg.includes('Network Error') ||
        msg.includes('Failed to fetch') ||
        msg.toLowerCase().includes('timeout') ||
        msg.includes('ECONNREFUSED') ||
        msg.includes('ENOTFOUND')) {
        return true;
    }
    // Axios: request sent, no response
    if (e.request && !e.response)
        return true;
    return false;
}
function mapPostgrestCode(code) {
    switch (code) {
        case 'PGRST116':
            return ERROR_MESSAGES.NOT_FOUND;
        case 'PGRST301':
            return ERROR_MESSAGES.NOT_FOUND;
        case 'PGRST406':
            return ERROR_MESSAGES.FORBIDDEN;
        default:
            return null;
    }
}
/**
 * Normalize any thrown/rejected value into a single user-facing string.
 */
export function toUserFacingMessage(error, options = {}) {
    const { fallback, maxMessageLength = DEFAULT_MAX_LEN } = options;
    if (error === null || error === undefined) {
        return fallback ?? ERROR_MESSAGES.SERVER_ERROR;
    }
    if (typeof error === 'string') {
        const t = error.trim();
        if (t && isLikelySafeApiMessage(t))
            return truncateMessage(t, maxMessageLength);
        return fallback ?? ERROR_MESSAGES.SERVER_ERROR;
    }
    if (typeof error !== 'object') {
        return fallback ?? ERROR_MESSAGES.SERVER_ERROR;
    }
    const err = error;
    const pgCode = typeof err.code === 'string' ? err.code : '';
    const status = typeof err.response === 'object' && err.response !== null
        ? err.response.status
        : undefined;
    const httpStatus = status ?? (typeof err.status === 'number' ? err.status : undefined);
    // Prefer HTTP status when present (e.g. Axios) over PostgREST code on the same object
    if (httpStatus !== undefined) {
        if (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
            return ERROR_MESSAGES.SERVICE_UNAVAILABLE;
        }
        if (httpStatus === 500) {
            return ERROR_MESSAGES.SERVER_ERROR;
        }
        if (httpStatus === 429) {
            return ERROR_MESSAGES.RATE_LIMIT;
        }
        if (httpStatus === 401) {
            return ERROR_MESSAGES.UNAUTHORIZED;
        }
        if (httpStatus === 403) {
            return ERROR_MESSAGES.FORBIDDEN;
        }
        if (httpStatus === 404) {
            return ERROR_MESSAGES.NOT_FOUND;
        }
        if (httpStatus === 408) {
            return ERROR_MESSAGES.REQUEST_TIMEOUT;
        }
        if (httpStatus >= 500) {
            return ERROR_MESSAGES.SERVER_ERROR;
        }
        if (httpStatus >= 400 && httpStatus < 500) {
            const data = typeof err.response === 'object' && err.response !== null
                ? err.response.data
                : undefined;
            const apiMsg = extractHttpMessage(data);
            if (apiMsg && isLikelySafeApiMessage(apiMsg)) {
                return truncateMessage(apiMsg, maxMessageLength);
            }
            if (httpStatus === 400 || httpStatus === 422) {
                return ERROR_MESSAGES.VALIDATION_ERROR;
            }
            return fallback ?? ERROR_MESSAGES.VALIDATION_ERROR;
        }
    }
    // Supabase / PostgREST client shape (no HTTP response)
    if (pgCode.startsWith('PGRST')) {
        const mapped = mapPostgrestCode(pgCode);
        if (mapped)
            return mapped;
        return fallback ?? ERROR_MESSAGES.SERVER_ERROR;
    }
    if (isNetworkLikeError(error)) {
        if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
            return 'You appear to be offline. Please check your connection and try again.';
        }
        return ERROR_MESSAGES.NETWORK_ERROR;
    }
    const msg = typeof err.message === 'string' ? err.message : '';
    if (msg && isLikelySafeApiMessage(msg) && !pgCode.startsWith('PGRST')) {
        return truncateMessage(msg, maxMessageLength);
    }
    return fallback ?? ERROR_MESSAGES.SERVER_ERROR;
}
/**
 * Prefer message attached by axios-auth interceptor, else compute.
 */
export function getUserFacingMessageFromAxiosError(error, options) {
    if (error && typeof error === 'object') {
        const u = error.userFacingMessage;
        if (typeof u === 'string' && u.trim())
            return u;
    }
    return toUserFacingMessage(error, options);
}
